import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconAlertCircle, IconArrowRight, IconCheck, IconFileText, IconShieldX } from "@tabler/icons-react";

import { Text, Button } from "@fragments";
import AICircle from "@components/AICircle";

import { MESSAGE } from "../../../hooks/useGenerateVideoScript";

import "./styles.scss";

/**
 * The conversation: the creator's messages on the right, the assistant's on
 * the left. A generated script is never printed inline — its row carries a
 * card that opens the preview column.
 *
 * The answer being written is a real row (PENDING) listing the reasoning
 * steps as they happen; it later turns into the script or the error under
 * the same id, so the row's orb settles from its active state in place.
 *
 * @param {Array}    messages      thread rows ({ id, kind, text?, script?, version? })
 * @param {Array}    steps         GENERATION_STEPS
 * @param {number}   stepIndex     index of the step currently running
 * @param {number}   previewId     id of the script row currently open in the preview
 * @param {object}   scrollRef     ref to the scrolling ancestor the thread lives in (defaults to the thread itself)
 * @param {function} onOpenPreview (messageId) => void
 * @param {function} onRetry       re-sends the failed request
 */
export default function ChatThread({ messages, steps, stepIndex, previewId, scrollRef, onOpenPreview, onRetry }) {
    const listRef = useRef(null);

    // Follow the conversation: the newest row (and each new step) scrolls
    // into view. The scroller is the column's scroll region (hero included),
    // so the header rides up with the thread instead of staying put.
    const lastKind = messages[messages.length - 1]?.kind;
    useEffect(() => {
        const el = scrollRef?.current || listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [scrollRef, messages.length, lastKind, stepIndex]);

    const lastId = messages[messages.length - 1]?.id;

    return (
        <div ref={listRef} className="chat-thread" role="log" aria-live="polite">
            {messages.map((message) => (
                <ChatMessage
                    key={message.id}
                    message={message}
                    steps={steps}
                    stepIndex={stepIndex}
                    active={message.id === previewId}
                    onOpenPreview={onOpenPreview}
                    onRetry={message.id === lastId && message.kind === MESSAGE.ERROR ? onRetry : undefined}
                />
            ))}
        </div>
    );
}

function ChatMessage({ message, steps, stepIndex, active, onOpenPreview, onRetry }) {
    const { t } = useTranslation("contentModals");
    const orbRef = useRef(null);

    // The orb is only "alive" while the answer is being written. Driven
    // through the orb's ref: no re-render of the WebGL subtree, and the
    // pending → answered change animates on the same DOM node.
    const pending = message.kind === MESSAGE.PENDING;
    const initialActive = useRef(pending).current;
    useEffect(() => {
        orbRef.current?.setActive(pending);
    }, [pending]);

    if (message.kind === MESSAGE.USER) {
        return (
            <div className="chat-message chat-message--user">
                <div className="chat-message__bubble">
                    <Text.p contrast={9}>{message.text}</Text.p>
                </div>
            </div>
        );
    }

    const kindClass = message.kind === MESSAGE.SCRIPT ? "" : `chat-message--${message.kind}`;

    return (
        <div className={`chat-message chat-message--assistant ${kindClass}`.trim()}>
            <div className="chat-message__avatar" aria-hidden="true">
                <AICircle ref={orbRef} size={20} active={initialActive} />
            </div>

            <div className="chat-message__body">
                {pending && <ReasoningSteps steps={steps} stepIndex={stepIndex} />}

                {message.kind === MESSAGE.SCRIPT && (
                    <>
                        <Text.p contrast={9}>
                            {message.version > 1
                                ? t("generateScript.chat.scriptRevised")
                                : t("generateScript.chat.scriptReady")}
                        </Text.p>

                        <ScriptCard
                            script={message.script}
                            version={message.version}
                            active={active}
                            onOpen={() => onOpenPreview(message.id)}
                        />
                    </>
                )}

                {message.kind === MESSAGE.ERROR && (
                    <>
                        <Text.p contrast={9} left={<IconAlertCircle />} className="chat-message__text">
                            {message.text}
                        </Text.p>
                        {onRetry && (
                            <div>
                                <Button size="small" color="overlay" onClick={onRetry} preventLoading>
                                    {t("common:actions.retry")}
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {message.kind === MESSAGE.BLOCKED && (
                    <Text.p contrast={9} left={<IconShieldX />} className="chat-message__text">
                        {message.text}
                    </Text.p>
                )}
            </div>
        </div>
    );
}

// What the server is doing right now, as a growing list: finished steps
// with a check, the current one with a pulse. Upcoming steps stay unseen.
function ReasoningSteps({ steps, stepIndex }) {
    const { t } = useTranslation("contentModals");
    const visible = steps.slice(0, Math.min(stepIndex, steps.length - 1) + 1);

    return (
        <ol className="chat-steps" aria-label={t("generateScript.chat.typing")}>
            {visible.map((step, index) => {
                const isCurrent = index === visible.length - 1;
                return (
                    <li key={step.key} className={`chat-steps__item ${isCurrent ? "is-current" : "is-done"}`}>
                        <span className="chat-steps__mark" aria-hidden="true">
                            {isCurrent ? <span className="chat-steps__pulse" /> : <IconCheck size={12} stroke={2.5} />}
                        </span>
                        <Text.span contrast={isCurrent ? 9 : 6}>
                            {t(`generateScript.steps.${step.key}.status`)}
                        </Text.span>
                    </li>
                );
            })}
        </ol>
    );
}

// The whole card is the affordance (role=button, keyboard-operable); the
// "Ver roteiro" chip inside is purely visual, never a nested button.
function ScriptCard({ script, version, active, onOpen }) {
    const { t } = useTranslation("contentModals");

    function handleKeyDown(event) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen();
        }
    }

    return (
        <div
            className={`script-card ${active ? "is-active" : ""}`.trim()}
            role="button"
            tabIndex={0}
            aria-pressed={active}
            aria-label={`${t("generateScript.chat.viewScript")}: ${script.title}`}
            onClick={onOpen}
            onKeyDown={handleKeyDown}
        >
            <div className="script-card__icon" aria-hidden="true">
                <IconFileText size={18} stroke={1.75} />
            </div>
            <div className="script-card__info">
                <Text.label contrast={9} className="script-card__title">{script.title}</Text.label>
                <Text.caption>
                    {t("generateScript.chat.version", { count: version })}
                    {" · "}
                    {t("generateScript.result.estimated", { count: script.estimatedMinutes })}
                </Text.caption>
            </div>
            <span className="script-card__cta" aria-hidden="true">
                <Text.label contrast={active ? 9 : 8} right={<IconArrowRight />}>
                    {t("generateScript.chat.viewScript")}
                </Text.label>
            </span>
        </div>
    );
}
